'use client';

import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useTranslations } from 'next-intl';

import type {
  ExtendedIndexUiState,
  InitialLabels,
} from '@/components/forms/CreateAlertForm/AlertSearchInterface';
import { AbstractModalV2 } from '@/components/modals/AbstractModalV2';
import { CreateAlertModal } from '@/components/modals/CreateAlertModal';
import type { EntityType } from '@/components/ProfileHeader/types';
import { ENTITY_ALERT_CONFIG } from '@/lib/alerts/entityAlertConfig';

import { SubscribeEntityAlertForm } from '../Forms/SubscribeEntityAlertForm';

export interface SubscribeEntityAlertModalProps {
  entityType: EntityType;
  /** One or more entity ids. Single entities pass a 1-element array. */
  entityIds: number[];
  entityName: string;
  /**
   * Optional per-id display names. Used when the ids do not all share a single
   * name (e.g. a parent univers subscribing to its many distinct sous-univers).
   * Falls back to `entityName` for any id missing from the map.
   */
  entityNamesById?: Record<number, string>;
}

/**
 * Builds a pre-filled searchState for the advanced alert modal. All ids flow
 * into the single Meilisearch attribute for the entity type (OR-combined).
 */
function buildSearchStateForEntity(
  entityType: EntityType,
  entityIds: number[],
): ExtendedIndexUiState {
  const { searchFilterKey } = ENTITY_ALERT_CONFIG[entityType];
  return {
    msl: {
      [searchFilterKey]: entityIds.map(String),
    },
  };
}

/**
 * Builds initial labels for the advanced alert modal. Each id maps to its own
 * name from `entityNamesById` when provided (the univers-parent case, where
 * every sous-univers has a distinct name), otherwise to the shared
 * `entityName`.
 */
function buildInitialLabels(
  entityType: EntityType,
  entityIds: number[],
  entityName: string,
  entityNamesById?: Record<number, string>,
): InitialLabels {
  const { labelKey } = ENTITY_ALERT_CONFIG[entityType];
  return {
    [labelKey]: Object.fromEntries(
      entityIds.map((id) => [String(id), entityNamesById?.[id] ?? entityName]),
    ),
  };
}

export const SubscribeEntityAlertModal =
  NiceModal.create<SubscribeEntityAlertModalProps>(
    ({ entityType, entityIds, entityName, entityNamesById }) => {
      const { visible, ...modal } = useModal();
      const t = useTranslations('Blocs.FormulaireInscriptionAlerteEntite');

      const handleOpenAdvancedAlert = () => {
        modal.hide();
        const searchState = buildSearchStateForEntity(entityType, entityIds);
        const initialLabels = buildInitialLabels(
          entityType,
          entityIds,
          entityName,
          entityNamesById,
        );
        NiceModal.show(CreateAlertModal, { searchState, initialLabels });
      };

      return (
        <AbstractModalV2
          className="max-w-lg"
          visible={visible}
          onClose={() => modal.hide()}
        >
          <SubscribeEntityAlertForm
            entityType={entityType}
            entityIds={entityIds}
            entityName={entityName}
            onSuccess={() => modal.hide()}
          />
          <div className="mt-4 text-sm text-gray-600">
            {t('fallback_alerte')}{' '}
            <button
              type="button"
              onClick={handleOpenAdvancedAlert}
              className="animated-underline text-primary-900"
            >
              {t('lien_creer_alerte')}
            </button>
          </div>
        </AbstractModalV2>
      );
    },
  );
